import type { FC } from 'react';
import { useUsersSelect } from 'src/modules/shared/hooks/users/select';
import { UNASSIGNED_USER_ID } from 'src/modules/communications/constants/filters.const';
import {
  TagDropdown,
  type TagDropdownOption,
} from 'src/modules/shared/components/molecules/dropdowns/tag-dropdown';
import type { DropdownPlacement } from 'src/modules/shared/components/molecules/dropdowns/dropdown';
import { useAllPersonAndSystemUsers } from 'src/modules/communications/hooks/users/system-users';
import { UserKind } from 'src/api/generated';
import { PiUserLight } from 'react-icons/pi';
import { AiRobotOutlineIcon } from 'src/modules/shared/components/atoms/indicators/ai-badge';

interface UserAssignDropdownProps {
  onUserSelect: (userId: number | null) => void;
  isLoading?: boolean;
  trigger: React.ReactNode;
  placement?: DropdownPlacement;
}

export const UserAssignDropdown: FC<UserAssignDropdownProps> = ({
  onUserSelect,
  isLoading,
  trigger,
  placement = 'bottom-start',
}) => {
  const { users } = useAllPersonAndSystemUsers();
  const { userOptions } = useUsersSelect({
    users,
    options: {
      autoSelectCurrentUser: false,
      clearSelectionForAdmin: false,
    },
  });

  const hasSystemUsers = userOptions.some(
    (opt) => opt.kind === UserKind.SYSTEM
  );

  const personOptions: TagDropdownOption[] = userOptions
    .filter((opt) => opt.kind === UserKind.PERSON)
    .map((opt) => ({
      id: opt.value,
      content: opt.label,
      icon: hasSystemUsers ? (
        <PiUserLight className="h-4 w-4 text-secondary-600" />
      ) : undefined,
    }));

  const systemOptions: TagDropdownOption[] = userOptions
    .filter((opt) => opt.kind === UserKind.SYSTEM)
    .map((opt) => ({
      id: opt.value,
      content: opt.label,
      icon: <AiRobotOutlineIcon className="h-4 w-4 text-secondary-600" />,
    }));

  if (hasSystemUsers && personOptions.length > 0 && systemOptions.length > 0) {
    personOptions[personOptions.length - 1] = {
      ...personOptions[personOptions.length - 1],
      separator: true,
    };
  }

  const dropdownOptions: TagDropdownOption[] = [
    {
      id: UNASSIGNED_USER_ID,
      // TODO: check when implementing i18n
      content: 'Sin asignar',
    },
    ...personOptions,
    ...(hasSystemUsers ? systemOptions : []),
  ];

  return (
    <TagDropdown
      content={trigger}
      variant="primary"
      appearance="outline"
      size="xs"
      options={dropdownOptions}
      onOptionSelect={(option) =>
        onUserSelect(
          option.id === UNASSIGNED_USER_ID ? null : Number(option.id)
        )
      }
      isLoading={isLoading}
      placement={placement}
    />
  );
};
